import { Seo, Reveal } from "../components/Seo";
import { PageHero } from "../components/blocks";
import { ContactForm } from "../components/forms";
import { CONTACT_INFO } from "../lib/site";

const NEXT_STEPS = [
  { n: "01", t: "We review your enquiry", d: "A member of the Softline Nigeria team reviews your message and the category you selected." },
  { n: "02", t: "We respond and schedule", d: "We come back to you to clarify requirements and, where useful, book a discovery session." },
  { n: "03", t: "You receive a proposal", d: "Where there is a fit, we present an implementation approach and engagement model." },
];

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact — Softline Nigeria"
        description="Request a consultation, product preview or partnership conversation with the Softline Technologies and Services Nigeria Limited team."
        path="/contact"
      />
      <PageHero
        eyebrow="Contact"
        title={["Let's talk about", "your requirements."]}
        lead="Tell us what you are trying to modernize, secure or launch. The right specialist from our team will respond."
        crumbs={[{ label: "Home", to: "/" }, { label: "Contact" }]}
      />

      <section className="bg-white" data-testid="contact-section">
        <div className="shell py-24 md:py-32 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="font-display font-bold text-2xl md:text-3xl">Send an enquiry</h2>
              <p className="mt-3 text-charcoal/65 leading-relaxed max-w-xl">
                Fields marked with an asterisk are required. We collect only what we need to respond to you.
              </p>
            </Reveal>
            <Reveal delay={0.1} className="mt-9">
              <ContactForm />
            </Reveal>
          </div>

          <aside className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="bg-charcoal text-white p-8 md:p-10" data-testid="contact-next-steps">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-lime">What happens next</p>
                <ol className="mt-7 space-y-7">
                  {NEXT_STEPS.map((s) => (
                    <li key={s.n} className="flex gap-5">
                      <span className="font-mono text-sm text-lime shrink-0">{s.n}</span>
                      <div>
                        <h3 className="font-semibold">{s.t}</h3>
                        <p className="text-sm text-softgrey leading-relaxed mt-1.5">{s.d}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
            <Reveal delay={0.22}>
              <div className="border border-charcoal/15 p-8 mt-6" data-testid="contact-details">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-charcoal/60">Direct channels</p>
                <dl className="mt-5 space-y-4 text-[15px]">
                  <div>
                    <dt className="font-semibold text-charcoal">Offices</dt>
                    <dd className="text-charcoal/65 mt-1" data-testid="contact-office-abuja">{CONTACT_INFO.offices[0]}</dd>
                    <dd className="text-charcoal/65 mt-1" data-testid="contact-office-lagos">{CONTACT_INFO.offices[1]}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold text-charcoal">Telephone</dt>
                    <dd className="mt-1 flex flex-col gap-1" data-testid="contact-phones">
                      {CONTACT_INFO.phones.map((p) => (
                        <a key={p} href={`tel:${p.replace(/[^+\d]/g, "")}`} className="text-charcoal/65 hover:text-charcoal transition-colors duration-200">
                          {p}
                        </a>
                      ))}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-semibold text-charcoal">Email</dt>
                    <dd className="mt-1" data-testid="contact-email">
                      <a href={`mailto:${CONTACT_INFO.email}`} className="text-charcoal/65 underline underline-offset-4 decoration-lime hover:text-charcoal transition-colors duration-200">
                        {CONTACT_INFO.email}
                      </a>
                    </dd>
                  </div>
                </dl>
                <p className="mt-6 text-xs leading-relaxed text-charcoal/50">
                  Enquiries submitted through this form are routed to our team inbox — you can also reach us directly
                  using the details above. Registration number: {CONTACT_INFO.regNo}.
                </p>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>
    </>
  );
}
